// RUM на Grafana Faro Web SDK (SAEX-4495, решение и схема - SAEX-4493).
// Приёмник - Grafana Alloy за маршрутом /client-health своего домена (ops,
// sa-observability/k8s/skeleton/rum-collector.yaml). Как читать данные - KB runbooks/rum-and-synthetics.md.
//
// Копия этого файла лежит в front, miniapp и help-center. Канон - front,
// src/shared/lib/rum/index.ts: правка идёт во все три репозитория.

import { getWebInstrumentations, initializeFaro, type TransportItem } from "@grafana/faro-web-sdk";

import { COLLECT_PATH, hostGroup, installGlue, sanitizeUrl } from "./glue";

export { COLLECT_PATH, hostGroup, installGlue, routeOf, sanitizeUrl } from "./glue";
export type { Glue } from "./glue";

export interface RumOptions {
  app: string;
  version: string;
  environment: string;
  // Доля загрузок страницы, у которых уходят web-vitals и Resource Timing. Ошибки - всегда.
  sampleRate?: number;
}

const PERF_EVENTS = new Set(["faro.performance.resource"]);
// Resource Timing - только по хостам, от которых зависит работа: свой, наши поддомены, фото.
const KEY_GROUPS = new Set(["self", "sa", "s3", "wbbasket", "ozone"]);

type Payload = {
  name?: string;
  type?: string;
  attributes?: Record<string, string>;
  context?: Record<string, string>;
};

// Чистит адреса и режет объём. Возвращает null, если событие не отправляем.
export function makeBeforeSend(sampled: boolean, base: string, selfHost: string) {
  return (item: TransportItem): TransportItem | null => {
    const page = item.meta.page;
    if (page?.url) page.url = sanitizeUrl(page.url, base);
    const p = item.payload as Payload;
    if (item.type === "measurement" && p.type === "web-vitals") {
      if (!sampled) return null;
      if (p.context?.resource_url) p.context.resource_url = sanitizeUrl(p.context.resource_url, base);
    }
    if (item.type === "event" && p.name && PERF_EVENTS.has(p.name) && p.attributes) {
      if (!sampled) return null;
      const raw = p.attributes.name ?? "";
      const clean = sanitizeUrl(raw, base);
      if (!clean) return null;
      const host = new URL(clean).host;
      const group = hostGroup(host, selfHost);
      if (!KEY_GROUPS.has(group) || clean.includes("/client-health")) return null;
      p.attributes.name = clean;
      p.attributes.host_group = group;
    }
    if (item.type === "event" && p.name === "faro.performance.navigation" && p.attributes?.name) {
      p.attributes.name = sanitizeUrl(p.attributes.name, base);
    }
    return item;
  };
}

let started = false;

export function initRum({ app, version, environment, sampleRate = 0.5 }: RumOptions): void {
  if (started || typeof window === "undefined") return;
  started = true;
  const sampled = Math.random() < sampleRate;
  const base = window.location.href;
  const selfHost = window.location.host.toLowerCase();

  // Склейка ставится до Faro: её обработчик visibilitychange должен сработать раньше
  // отправки пачки Faro, иначе счётчики последних секунд страницы потеряются.
  let faroPush: ((name: string, attrs: Record<string, string>) => void) | null = null;
  const queue: Array<[string, Record<string, string>]> = [];
  installGlue({
    pushEvent: (name, attrs) => (faroPush ? faroPush(name, attrs) : queue.push([name, attrs])),
  });

  const faro = initializeFaro({
    url: COLLECT_PATH,
    app: { name: app, version, environment },
    instrumentations: getWebInstrumentations({ captureConsole: false, enablePerformanceInstrumentation: true }),
    trackResources: true,
    webVitalsInstrumentation: { trackAttributionSources: true },
    sessionTracking: { enabled: true, persistent: false },
    // Пачка не больше 64 КБ: лимит тела fetch keepalive и маршрута Traefik.
    batching: { enabled: true, itemLimit: 30, sendTimeout: 5000 },
    ignoreErrors: ["ResizeObserver loop", /extension:\/\//i],
    beforeSend: makeBeforeSend(sampled, base, selfHost),
  });
  faroPush = (name, attrs) => faro.api.pushEvent(name, attrs, "rum");
  for (const [name, attrs] of queue.splice(0)) faroPush(name, attrs);
}
